import React, { useEffect, useState } from 'react';
import { Box, LinearProgress, Paper, Stack, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import JourneyStepIcon from './Common/JourneyStepIcon';
import ActionFlowHelp from './ProjectDag/ActionFlowHelp';

const PHASES = ['understand', 'prepare', 'check'];
const PHASE_ICONS = {
  understand: ChatBubbleOutlineRoundedIcon,
  prepare: AutoAwesomeRoundedIcon,
  check: FactCheckOutlinedIcon,
};

const STAGE_PHASES = {
  request_sent: 'understand', planning_start: 'understand', reading_attachments: 'understand',
  planning_model: 'understand', planning_response: 'understand', planning_validation: 'understand',
  planning_retry: 'understand', planning_complete: 'understand', preparing: 'prepare',
  opencode_generation: 'prepare', validation: 'check', repairing: 'check',
  accepted: 'check', runtime_testing: 'check', saved: 'check',
};

export const preparationProgressPhase = (stage) => Object.prototype.hasOwnProperty.call(STAGE_PHASES, stage) ? STAGE_PHASES[stage] : null;
const duration = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
const updateLabel = (entry, t) => [t(`connector_creator.journey.updates.${entry.stage}`),
  entry.attempt > 1 ? t('connector_creator.journey.update_attempt', { count: entry.attempt }) : '',
].filter(Boolean).join(' · ');

export function ConnectorPreparationSteps({ phase, creatorType = 'connector', updates = [] }) {
  const { t } = useTranslation();
  const activeStep = Math.max(0, PHASES.indexOf(phase));
  return <Stepper activeStep={activeStep} alternativeLabel role="list"
    aria-label={t(creatorType === 'action' ? 'connector_creator.journey.action_steps_label' : 'connector_creator.journey.steps_label')}>
    {PHASES.map((step, index) => {
      const latest = updates.filter((entry) => entry.phase === step).slice(-1)[0];
      return <Step key={step} role="listitem" completed={index < activeStep}
      aria-current={index === activeStep ? 'step' : undefined}>
      <StepLabel icon={<JourneyStepIcon symbol={PHASE_ICONS[step]} active={index === activeStep}
        completed={index < activeStep} />}>
        {t(`connector_creator.journey.step_${step}`)}
        {latest && <Typography component="span" variant="caption" color="text.secondary"
          sx={{ display: 'block', mt: 0.5, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
          {index < activeStep ? t('connector_creator.journey.phase_finished') : updateLabel(latest, t)}
        </Typography>}
      </StepLabel>
    </Step>;
    })}
  </Stepper>;
}

export default function ConnectorPreparationProgress({ phase, busy, waiting, error, reviewingAnswers, stage, updateToken, showSteps = true, creatorType = 'connector', updates = [], startedAt }) {
  const { t } = useTranslation();
  const [waitingForUpdate, setWaitingForUpdate] = useState(false);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!busy) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [busy]);
  useEffect(() => {
    setWaitingForUpdate(false);
    if (!busy) return undefined;
    const timer = setTimeout(() => setWaitingForUpdate(true), 20000);
    return () => clearTimeout(timer);
  }, [busy, phase, stage, updateToken]);
  const activity = reviewingAnswers ? 'reviewing_answers'
    : stage === 'repairing' ? 'repairing' : phase;
  const title = t(`connector_creator.journey.${creatorType === 'action' && ['prepare', 'check'].includes(activity) ? `action_${activity}` : activity}`);
  const helpKey = creatorType === 'action' && ['prepare', 'check'].includes(phase) ? `help_action_${phase}` : `help_${phase}`;
  const ActivityIcon = PHASE_ICONS[phase] || PHASE_ICONS.understand;
  const latestUpdates = updates.filter((entry) => entry.phase === phase).slice(-3);
  const elapsed = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : null;
  const lastUpdate = updates[updates.length - 1];
  const silence = lastUpdate ? Math.max(0, Math.floor((now - lastUpdate.at) / 1000)) : null;
  return <Stack spacing={2.5}>
    {showSteps && <ConnectorPreparationSteps phase={phase} creatorType={creatorType} updates={updates} />}
    {!waiting && !error && busy && <Paper variant="outlined" sx={{ p: 2, borderRadius: 2,
      bgcolor: 'background.paper', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
        <ActivityIcon color="primary" sx={{ fontSize: 22, mr: 0.5, flexShrink: 0 }} />
        <Typography role="status" aria-live="polite" variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        <ActionFlowHelp topic={title}>{t(`connector_creator.journey.${helpKey}`)}</ActionFlowHelp>
      </Stack>
      <LinearProgress aria-label={title} sx={{ height: 4, borderRadius: 2 }} />
      {latestUpdates.length > 0 && <Stack component="ol" role="log" aria-live="polite" aria-relevant="additions text"
        aria-label={t('connector_creator.journey.updates_label')} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0, mt: 2 }}>
        {latestUpdates.map((entry, index) => <Stack component="li" direction="row" spacing={1} alignItems="baseline"
          key={`${entry.stage}-${entry.attempt}-${entry.at}`}>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {duration(Math.max(0, Math.floor((entry.at - (startedAt || entry.at)) / 1000)))}
          </Typography>
          <Typography component="span" variant="body2" color={index === latestUpdates.length - 1 ? 'text.primary' : 'text.secondary'}>
            {updateLabel(entry, t)}
          </Typography>
        </Stack>)}
      </Stack>}
      {elapsed !== null && <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {t('connector_creator.journey.elapsed', { time: duration(elapsed) })}
        </Typography>
        {silence >= 20 && <Typography variant="caption" color="text.secondary">
          {t('connector_creator.journey.last_update', { time: duration(silence) })}
        </Typography>}
      </Box>}
      {waitingForUpdate && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {t('connector_creator.journey.waiting_update')}
      </Typography>}
    </Paper>}
  </Stack>;
}
